/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ICartLineItemViewProps,
    ICartlinesViewProps,
    IOrderSummaryErrors
} from '@msdyn365-commerce-modules/cart';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    ILabeledDropdownOption,
    INodeProps,
    ITelemetryContent, LabeledDropdown,
    Node, ILabeledDropdownOnChangeNotification
} from '@msdyn365-commerce-modules/utilities';
import { AsyncResult, ChannelInventoryConfiguration } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import {
    IProductListViewModeProps,
    ProductListViewMode,
    ProductListViewModeComponent
} from '../../components/product-list-component/components/product-list-view-mode';
import {
    cartLines2ProductList,
    ProductListComponent,
    ProductListProduct
} from '../../components/product-list-component/product-list-component';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import { IDlvryCartProps as ICartExtensionProps, IDlvryCartResources as ICartResources, preferredViewMode } from './dlvry-cart.props.autogenerated';
import { ISortByViewProps } from '../dlvry-search-result-container/dlvry-search-result-container';
import { useState } from 'react';
import { ICartViewProps } from './dlvry-cart';
import { IOrderSummaryLinesExt, IProductError } from '../../shared-utils/types';
import ProductListCompactComponent from '../../components/product-list-compact-component/product-list-compact-component';
import { getCustomerAgreements } from '../../shared-utils/agreement-product-utils';
import { getOriginalPrice } from '../../themes/dlvry-adventureworks/views/components/price.component';
import { getAllCharges } from '../../shared-utils/cart-utils';

export interface ICartExtData {
    channelInventoryConfiguration: AsyncResult<ChannelInventoryConfiguration>;
}

const cartViewModeCookieName = 'i_365_cartViewMode_cookie';
const cartSortOptionCookieName = 'i_365_cartSortOptione_cookie';

const SortByKeys = {
    OldestFirst: 'sortByOldestFirst',
    NameAsc: 'sortByOptionNameAsc',
    NameDesc: 'sortByOptionNameDesc',
    PriceAsc: 'sortByOptionPriceAsc',
    PriceDesc: 'sortByOptionPriceDesc',
    LineAmountAsc: 'sortByOptionLineAmountAsc',
    LineAmountDesc: 'sortByOptionLineAmountDesc'
} as const;

type SortByKey = (typeof SortByKeys)[keyof typeof SortByKeys];

const getViewModeFromCookie = (props: ICartViewProps): ProductListViewMode => {
    const preferred = props.config.preferredViewMode === preferredViewMode.compactView
        ? ProductListViewMode.compactView
        : ProductListViewMode.listView;
    const viewMode = GetCookie(props.context.request.cookies, cartViewModeCookieName);
    return viewMode ? +viewMode : preferred || ProductListViewMode.listView;
};
const getSortOptionFromCookie = (props: ICartViewProps): SortByKey => {
    const SortOption = GetCookie(props.context.request.cookies, cartSortOptionCookieName);
    const validValues = Object.values(SortByKeys);
    return validValues.includes(SortOption as SortByKey) ? (SortOption as SortByKey) : SortByKeys.OldestFirst;
};

/**
 * Render Cart Lines group title.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param count - Count of products.
 * @returns JSX Element.
 */
// Unused per Task 2890
// @ts-ignore
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const itemCountOne: number = 1;
    const suffix = count > itemCountOne ? itemsLabel : itemLabel;
    const countTitle = `(${count} ${suffix})`;
    return (
        <>
            <Node className='msc-cart-lines-group-wrapper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wrapper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {', '}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
                <p className='msc-cart-lines-group-wrapper__bopis-heading-title-ct'>{countTitle}</p>
            </Node>
        </>
    );
};

/**
 * Render error block.
 * @param errorData - Error component.
 * @returns JSX Element.
 */
const renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        <Node className='msc-order-summary__errors'>
            <Node {...errorData.Wrapper}>
                {errorData.header}
                {errorData.errors}
            </Node>
        </Node>
    );
};

/**
 * Render invoice summary.
 * @param invoiceSummaryLines - Invoice summary component.
 * @param orderSummaryItems - Order summary component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...orderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Render order summary.
 * @param orderSummaryLines - Order summary component.
 * @param orderSummaryItems - Order summary items component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLinesExt | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }

    let totalDiscounts = orderSummaryLines.totalDiscounts;

    // Remove '-' sign from price
    if (totalDiscounts && (totalDiscounts as React.ReactElement).props.price < 0) {
        const totalDiscountAmount = props.data.cart.result?.cart.CartLines?.reduce((sum, line) => {
            // Old method that didn't work correctly with mix and match discounts
            /* const discountAmount = getDiscountLines(line.DiscountLines).reduce((sum, discount) => {
                return sum += discount.EffectiveAmount || 0;
            }, 0); */

            const originalPrice = getOriginalPrice({
                CustomerContextualPrice: line.TotalAmount,
                BasePrice: line.NetPrice,
                DiscountLines: line.DiscountLines
            });

            const discountAmount = originalPrice - (line.TotalAmount || 0);

            return sum += discountAmount;
        }, 0);

        if (!totalDiscountAmount) {
            totalDiscounts = null;
        } else {
            totalDiscounts = React.cloneElement(totalDiscounts as React.ReactElement, {
                price: Math.abs(totalDiscountAmount)
            });
        }
    }

    const subAndOrderTotal = props.cart?.cart.CartLines
        ?.map(x => (x.TotalAmount ?? (x.Price! * (x.Quantity || 1))))
        .reduce((a, b) => a + b, 0);

    const subtotal = React.cloneElement(orderSummaryLines.subtotal as React.ReactElement, {
        price: subAndOrderTotal
    });

    const allCharges = getAllCharges(props.cart?.cart);

    const orderTotal = React.cloneElement(orderSummaryLines.orderTotal as React.ReactElement, {
        price: (subAndOrderTotal ?? 0) + (allCharges ? allCharges.reduce((a, b) => a + b.TotalCalculatedAmount, 0) : 0)
    });

    // Shipping field shouldn't be used, but in case it is, hide it when its value is 0/undefined
    const shipping = ((orderSummaryLines.shipping as JSX.Element).props.price || 0) > 0
        ? orderSummaryLines.shipping
        : null;

    return (
        <Node {...orderSummaryItems}>
            {subtotal}
            {totalDiscounts ? totalDiscounts : null}
            {shipping}
            {orderSummaryLines.charges}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderTotal}
        </Node>
    );
};

/**
 * Render error block.
 * @param promotions - The module view props.
 * @returns JSX Element.
 */
const renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                <Node className='msc-cart__promotion_link-section'>{promotions.promotionSeeAllLink}</Node>
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Render order summary checkout.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const renderOrderSummaryCheckout = (props: ICartViewProps): JSX.Element | undefined => {
    return (
        <Node className='msc-order-summary__checkout'>
            {props.cart?.hasInvoiceLine || !props.config.displayPromoCodeBlock ? null : props.promoCode}
            <Node className='msc-order-summary__checkout__action'>
                {props.checkoutAsSignInUserButton}
                {props.checkoutAsGuestButton}
                {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                    <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                ) : null}
            </Node>
        </Node>
    );
};

/**
 * Render order summary checkout button.
 * @param props - The module view props.
 * @returns JSX Element.
 */
// @ts-expect-error
const renderOrderSummaryCheckoutButton = (props: ICartViewProps): JSX.Element | undefined => {
    return (
        <Node className='msc-order-summary__checkout-button'>
            {props.checkoutAsSignInUserButton}
            {props.checkoutAsGuestButton}
        </Node>
    );
};

/**
 * Render cart view.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const CartView: React.FC<ICartViewProps & ICartExtensionProps<ICartExtData>> = (props: ICartViewProps & ICartExtensionProps<ICartExtData>) => {

    const sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: SortByKeys.OldestFirst, value: props.resources.sortByOldestFirst },
        { key: SortByKeys.NameAsc, value: props.resources.sortByOptionNameAsc },
        { key: SortByKeys.NameDesc, value: props.resources.sortByOptionNameDesc },
        { key: SortByKeys.PriceAsc, value: props.resources.sortByOptionPriceAsc },
        { key: SortByKeys.PriceDesc, value: props.resources.sortByOptionPriceDesc },
        { key: SortByKeys.LineAmountAsc, value: props.resources.sortByOptionLineAmountAsc },
        { key: SortByKeys.LineAmountDesc, value: props.resources.sortByOptionLineAmountDesc }
    ];
    // Get view mode first from cookies, then from config, then default to list view if none are set
    const [viewMode, setViewMode] = React.useState<ProductListViewMode>(getViewModeFromCookie(props));
    const [sortedBy, setSortedBy] = useState<SortByKey>(getSortOptionFromCookie(props));
    const totalItemCountOne: number = 1;
    // @ts-expect-error
    const updatedTitle = (
        <h1 className='msc-cart__heading'>
            {props.resources.cartTitle}
            <span className='msc-cart__heading-count-items'>
                {` (${Number(props.cart?.totalItemsInCart)} ${props.cart?.totalItemsInCart !== totalItemCountOne ? 'items' : 'item'})`}
            </span>
        </h1>
    );

    const _onProductRemove = async (product: ProductListProduct) => {
        const line = props.cartlines?.find(cartLine => cartLine?.data?.product?.RecordId === product.RecordId);
        if (!line || !line.data?.cartline) {
            return;
        }
        props.removeItemClickHandler(line.data.cartline);
    };

    /**
     * Render Cart Lines.
     * @param props - The cart line props.
     * @param cartLines - The cart line view props.
     * @param resources - Module resource strings.
     * @param storeSelector - Store selector element.
     * @param waitingComponent - Waiting component element.
     * @param cartLoadingStatus - Cart loading status string.
     * @param isCartDataResult - Flag to check if there is cart data.
     * @param telemetryContent - The telemetry object.
     * @param isMultiplePickUp - Flag to check if there multiple pickups.
     * @param emailDeliveryModeCode - The email delivery mode code.
     * @returns JSX Element.
     */
    const renderCartlines = (
        props: ICartViewProps & ICartExtensionProps<ICartExtData>,
        cartLines: ICartlinesViewProps[] | undefined,
        resources: ICartResources,
        storeSelector: React.ReactNode | undefined,
        waitingComponent: React.ReactNode,
        cartLoadingStatus: string,
        isCartDataResult: boolean,
        telemetryContent?: ITelemetryContent,
        isMultiplePickUp?: boolean,
        emailDeliveryModeCode?: string
    ): JSX.Element[] | JSX.Element => {
        if (cartLoadingStatus) {
            return <>{cartLoadingStatus}</>;
        }

        const actionsAvailable = {
            removeAction: true,
            addToWishlistAction: true
        };

        if (cartLines) {
            if (props.searchTerm) {
                cartLines = cartLines
                    .filter(cartLine => cartLine.data?.product?.Name?.toLowerCase().includes(props.searchTerm.toLowerCase())
                        || cartLine.data?.product?.ItemId?.toLowerCase().includes(props.searchTerm.toLowerCase()));
            }

            cartLines?.sort((a, b) => {
                if (sortedBy === 'sortByOldestFirst') {
                    return 0;
                } else if (sortedBy === 'sortByOptionNameAsc' && a.data?.product?.Name && b.data?.product?.Name) {
                    return a.data?.product?.Name.localeCompare(b.data.product.Name, props.context.request.locale);
                } else if (sortedBy === 'sortByOptionNameDesc' && a.data?.product?.Name && b.data?.product?.Name) {
                    return b.data?.product?.Name.localeCompare(a.data.product.Name, props.context.request.locale);
                } else if (sortedBy === 'sortByOptionPriceAsc' && a.data?.cartline?.Price && b.data?.cartline?.Price) {
                    return a.data.cartline.Price - b.data.cartline.Price;
                } else if (sortedBy === 'sortByOptionPriceDesc' && a.data?.cartline?.Price && b.data?.cartline?.Price) {
                    return b.data.cartline.Price - a.data.cartline.Price;
                } else if (sortedBy === 'sortByOptionLineAmountAsc' && a.data?.cartline.Price && b.data?.cartline.Price && a.data?.cartline.Quantity && b.data?.cartline.Quantity) {
                    return (a.data.cartline.Price * a.data.cartline.Quantity) - (b.data.cartline.Price * b.data.cartline.Quantity);
                } else if (sortedBy === 'sortByOptionLineAmountDesc' && a.data?.cartline.Price && b.data?.cartline.Price && a.data?.cartline.Quantity && b.data?.cartline.Quantity) {
                    return (b.data.cartline.Price * b.data.cartline.Quantity) - (a.data.cartline.Price * a.data.cartline.Quantity);
                } else {
                    return -0;
                }
            });
        }

        const errors: IProductError[] | undefined = cartLines
            ?.filter(x => x.data?.product?.RecordId && x.error)
            .map(x => ({
                RecordId: x.data!.product!.RecordId,
                Error: `${x.data!.product!.Name} ${x.error!}`,
                Type: 'warning'
            }));

        // Props.CartlinesWrapper is only required on cart lines and when multiple pickup mode is disabled. This is also required to handle the empty cart styling.
        // Else, empty cart multiple pickup mode is disabled falls under the other cart line wrapper which is creating issue with Empty cart scenario.
        if (cartLines && viewMode === ProductListViewMode.listView) {
            return (
                <ProductListComponent
                    products={cartLines2ProductList(cartLines, props.productAvailabilites, props.context, props.activePrices)}
                    simpleProducts={props.data.products.result || []}
                    unitConversions={props.unitConversions}
                    productErrors={errors}
                    customerInformation={props.data.customerInformation.result}
                    customerAgreements={getCustomerAgreements(props.context, props.data.organizationInformation.result?.Attributes)}
                    context={props.context}
                    resources={{
                        ...props.resources,
                        removeButtonText: props.resources.removeCartButtonText,
                        addToWishlistText: props.resources.addToWishlistButtonText,
                        removeFromWishlistText: props.resources.removeFromWishlistButtonText
                    }}
                    moduleType={props.typeName}
                    moduleId={props.id}
                    imageSettings={props.config.imageSettings}
                    allowMultiSelect
                    disableQuantityInputForProducts={props.disableQuantityInputForProducts}
                    actionsAvailable={actionsAvailable}
                    allowZeroQuantity={false}
                    showAvailability
                    showTotalPrices
                    smallerImage
                    wishlists={props.data.wishlists.result}
                    onProductRemove={_onProductRemove}
                    breakoffProducts={props.breakoffProducts}
                    onBreakoffChange={props.onBreakoffChange}
                    selectedProducts={props.selectedProducts}
                    onUpdateSelection={props.onUpdateSelection}
                    onUpdateQuantity={(product, quantity, applyImmediately) => {
                        const line = props.cartlines?.find(x => x.data?.cartline?.LineId === product.CartLine?.LineId);
                        if (line?.data?.cartline && line.data.cartline.Quantity !== quantity) {
                            props.updateCartlineQuantity(line.data.cartline, quantity, applyImmediately);
                        }
                    }}
                    onUpdateOrderTemplates={props.onUpdateOrderTemplates}
                    retrieveOrderTemplates={props.retrieveOrderTemplates}
                />
            );
        } else if (cartLines && viewMode === ProductListViewMode.compactView) {
            return (
                <ProductListCompactComponent
                    mode='cart'
                    context={props.context}
                    moduleId={props.id}
                    moduleType={props.typeName}
                    resources={{
                        ...props.resources,
                        removeButtonText: props.resources.removeCartButtonText,
                        addToWishlistText: props.resources.addToWishlistButtonText,
                        removeFromWishlistText: props.resources.removeFromWishlistButtonText
                    }}
                    imageSettings={props.config.imageSettings}
                    allowMultiSelect
                    disableQuantityInputForProducts={props.disableQuantityInputForProducts}
                    allowZeroQuantity={false}
                    showPrices
                    showTotalPrices
                    products={cartLines2ProductList(cartLines, props.productAvailabilites, props.context, props.activePrices)}
                    simpleProducts={props.data.products.result || []}
                    customerInformation={props.data.customerInformation.result}
                    organizationInformation={props.data.organizationInformation.result}
                    cart={props.data.cart.result}
                    unitConversions={props.unitConversions}
                    wishlists={props.data.wishlists.result}
                    productErrors={errors}
                    selectedProducts={props.selectedProducts}
                    breakoffProducts={props.breakoffProducts}
                    onProductRemove={_onProductRemove}
                    onUpdateSelection={props.onUpdateSelection}
                    onUpdateQuantity={(product, quantity, applyImmediately) => {
                        const line = props.cartlines?.find(x => x.data?.cartline?.LineId === product.CartLine?.LineId);
                        if (line?.data?.cartline && line.data.cartline.Quantity !== quantity) {
                            props.updateCartlineQuantity(line.data.cartline, quantity, applyImmediately);
                        }
                    }}
                    onBreakoffChange={props.onBreakoffChange}
                    onUpdateOrderTemplates={props.onUpdateOrderTemplates}
                    retrieveOrderTemplates={props.retrieveOrderTemplates}
                />
            );
        }

        return isCartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <div className='msc-cart-line'>{props.slots.emptyCart}</div>
            </div>
        ) : (
            <>{waitingComponent}</>
        );
    };

    const setCardViewMode = () => {
        setViewMode(ProductListViewMode.cardView);
        updateCurrentViewMode(ProductListViewMode.cardView);
    };

    const setListViewMode = () => {
        setViewMode(ProductListViewMode.listView);
        updateCurrentViewMode(ProductListViewMode.listView);
    };

    const setCompactViewMode = () => {
        setViewMode(ProductListViewMode.compactView);
        updateCurrentViewMode(ProductListViewMode.compactView);
    };

    const updateCurrentViewMode = (_viewMode: ProductListViewMode) => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(_viewMode);
        SetCookie(props.context.request.cookies, newCookieValue, cartViewModeCookieName, cookieAge, true);
    };
    const updateSortOptionMode = (_SortOption: any) => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(_SortOption);
        SetCookie(props.context.request.cookies, newCookieValue,cartSortOptionCookieName , cookieAge, true);
    };

    const viewModeProps: IProductListViewModeProps = {
        viewMode: viewMode,
        cardButtonTxt: props.resources.viewModeCardText,
        listButtonTxt: props.resources.viewModeListText,
        compactButtonTxt: props.resources.viewModeCompact,
        disableView: {
            cardView: true
        },
        setCardViewMode: setCardViewMode,
        setListViewMode: setListViewMode,
        setCompactViewMode: setCompactViewMode
    };

    const _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        setSortedBy(notification.selectedOption.key as SortByKey);
        updateSortOptionMode(notification.selectedOption.key);
    };
    const _getSortingDropDown = (): ISortByViewProps => {
        const activeDropdown = sortByDropdownOptions.find((sortOption)=>{
            return sortOption.key===sortedBy;
        });
        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={sortByDropdownOptions}
                selectedOption={activeDropdown}
                onSelectOption={_updateSortByDropdown}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };
    const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
        const { SortingContainer, sortByDropDown } = props;
        if (sortByDropDown && !isRecoSearchPage) {
            return <Node {...SortingContainer}>{sortByDropDown}</Node>;
        }
        return null;
    };
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.title}
            <div className={`${props.className}__grid`}>
                {!props.cart?.hasInvoiceLine && props.promotionOptions &&  renderPromotions(props.promotionOptions)}
                <div className={'cart-view-page'}>
                    {props.actionBar}
                    <div className='cart-view-page__top'>
                        <div className='msc-search-bar-wrapper'>
                            {props.searchBar}
                        </div>
                        <div className='dlvry-cart-sort-and-view-mode-wrapper'>
                            <div className='ms-search-result-wrapper-sort-by-category'>
                                {!isRecoSearchPage && props.cartlines && props.cartlines.length > 0 && renderSort(_getSortingDropDown())}
                            </div>
                            <ProductListViewModeComponent {...viewModeProps} />
                        </div>
                    </div>
                    {renderCartlines(
                        props,
                        props.cartlines,
                        props.resources,
                        props.storeSelector,
                        props.waitingComponent,
                        props.cartLoadingStatus,
                        props.cartDataResult
                    )}
                </div>
                {props.orderSummaryHeading && (
                    <Node {...props.OrderSummaryWrapper}>
                        <Node className='msc-order-summary__items'>
                            {props.orderSummaryHeading}
                            {props.cart?.hasInvoiceLine
                                ? renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                                : renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                        </Node>
                        {renderErrorBlock(props.OrderSummaryErrors)}
                        {renderOrderSummaryCheckout(props)}
                    </Node>
                )}
            </div>
            {props.storeSelector}
            {props.importModal}
        </div>
    );
};

export default CartView;
